import React, { PropsWithChildren, ReactElement, ReactNode, useState } from 'react';
import {
  LayoutRectangle,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { Border, Color, FontFamily, FontSize, Padding } from '@/GlobalStyles';
import { useModalScreenPosition } from '@/components/Navigation/ModalScreen';
import { ImageProps } from 'expo-image';
import { isArray } from 'lodash';
import { useIsFocused } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { Router } from 'expo-router/build/types';
import Button, { ButtonsProps } from '@/components/Button';

type MenuProps = PropsWithChildren<{
  anchor: ReactElement<ViewProps | ImageProps>;
  inScreenModal?: boolean;
  position?: 'right' | 'left' | 'before';
  topPosition?: 'top' | 'under' | 'bottom' | 'center';
  anchorContainerStyle?: ViewStyle;
  anchorMode?: ButtonsProps['mode'];
  posX?: number;
  posY?: number;
  isSimplePressable?: boolean;
  modalStyle?: ViewStyle;
}>;

const getNewChildren = (
  children: ReactNode,
  setVisible: (visible: boolean) => void,
  router: Router
): ReactNode => {
  return (!isArray(children) ? [children] : children).map((child) => {
    if (child === null) {
      return null;
    }
    if (typeof child === 'string') {
      return child;
    }
    if (child.props.children) {
      return React.cloneElement(child, {
        children: getNewChildren(child.props.children, setVisible, router),
      });
    }
    if (child.props.href || child.props.onPress) {
      return React.cloneElement(child, {
        onPress: () => {
          setVisible(false);
          if (child.props.href || child.props.onPress) {
            if (Platform.OS === 'ios') {
              //bug iOs à cause de la gestion multiple de modal
              setTimeout(() => {
                if (child.props.onPress) {
                  child.props.onPress();
                }
                if (child.props.href) {
                  router.push(child.props.href);
                }
              }, 50);
            } else {
              if (child.props.onPress) {
                child.props.onPress();
              }
              if (child.props.href) {
                router.push(child.props.href);
              }
            }
          }
        },
      });
    } else {
      return child;
    }
  });
};

const Menu = (props: MenuProps) => {
  const {
    anchor,
    children,
    inScreenModal = false,
    topPosition = 'top',
    position = 'right',
    anchorContainerStyle,
    posX,
    posY,
    anchorMode = 'text',
    isSimplePressable = false,
    modalStyle,
  } = props;

  const ref = React.createRef<View>();

  const [visible, setVisible] = useState(false);

  const isFocused = useIsFocused();

  const router = useRouter();

  const [menuContainerLayout, setMenuContainerLayout] = useState<LayoutRectangle | undefined>();
  const [anchorLayout, setAnchorLayout] = useState<LayoutRectangle | undefined>();

  const { landscape, x, y } = useModalScreenPosition(1024);
  const { height } = useWindowDimensions();

  const addX = Platform.OS === 'web' && landscape && inScreenModal ? x.to : 0;
  const addY = Platform.OS === 'web' && !landscape && inScreenModal ? y.to : 0;

  let top =
    (anchorLayout?.y ?? 0) +
    addY +
    (topPosition === 'under' ? anchorLayout?.height ?? 0 : 0) +
    (posY ?? 0);
  let left =
    (anchorLayout?.x ?? 0) +
    addX +
    (position === 'right' ? (anchorLayout?.width ?? 0) - (menuContainerLayout?.width ?? 0) : 0) +
    (posX ?? 0);

  if (topPosition === 'bottom') {
    top -= menuContainerLayout?.height ?? 0;
  }

  if (topPosition === 'center') {
    top -= (menuContainerLayout?.height ?? 0) / 2 - (anchorLayout?.height ?? 0) / 2;
  }

  if (position === 'before') {
    left -= menuContainerLayout?.width ?? 0;
  }

  if (left < 10) {
    left = 10;
  }

  if (top + (menuContainerLayout?.height ?? 0) > height) {
    top -= menuContainerLayout?.height ?? 0;
    if (topPosition === 'top') {
      top += anchorLayout?.height ?? 0;
    }
  }

  const newChildren = getNewChildren(children, setVisible, router);

  return (
    <>
      {isSimplePressable ? (
        <View
          ref={ref}
          onLayout={({ nativeEvent }) => {
            ref.current?.measureInWindow((x, y, width, height) => {
              setAnchorLayout({ x, y, width, height });
            });
          }}
          style={anchorContainerStyle}
        >
          <Pressable
            onPress={() => {
              ref.current?.measureInWindow((x, y, width, height) => {
                setAnchorLayout({ x, y, width, height });
                setVisible(true);
              });
            }}
          >
            {anchor}
          </Pressable>
        </View>
      ) : (
        <Button
          ref={ref}
          onLayout={({ nativeEvent }) => {
            ref.current?.measureInWindow((x, y, width, height) => {
              setAnchorLayout({ x, y, width, height });
            });
          }}
          mode={anchorMode}
          compact
          outerStyle={anchorContainerStyle}
          onPress={() => {
            ref.current?.measureInWindow((x, y, width, height) => {
              setAnchorLayout({ x, y, width, height });
              setVisible(true);
            });
          }}
        >
          {anchor}
        </Button>
      )}
      <Modal
        transparent
        visible={visible && isFocused}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={styles.modalContainerOverlay}>
          <Pressable style={[styles.modalContainerBg]} onPress={() => setVisible(false)} />
          <View
            style={[styles.menuDropdown, { top, left, opacity: menuContainerLayout ? 1 : 0 }]}
            onLayout={({ nativeEvent }) => setMenuContainerLayout(nativeEvent.layout)}
          >
            <View style={[styles.modal, modalStyle]}>{newChildren}</View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default Menu;

const styles = StyleSheet.create({
  modalContainerOverlay: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(113, 113, 113, 0.13)',
  },
  modalContainerBg: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    left: 0,
    top: 0,
  },
  topSpaceBlock: {
    paddingHorizontal: Padding.p_11xl,
  },
  monCompteMenuTypo: {
    textAlign: 'right',
    color: Color.MainNoir,
    fontFamily: FontFamily.labelMedium,
    fontWeight: '500',
    lineHeight: 18,
    fontSize: FontSize.labelMedium_size,
  },
  top: {
    paddingTop: Padding.p_lg,
    paddingBottom: Padding.p_smi,
    marginBottom: Padding.p_smi,
    borderBottomWidth: 1,
    borderColor: Color.colorGray_200,
  },
  menuButton: {
    paddingVertical: Padding.p_xs,
  },
  bottom: {
    paddingBottom: Padding.p_11xl,
  },
  modal: {
    borderRadius: Border.br_xs,
    backgroundColor: Color.blanc,
    width: 210,
  },
  filterModal: {
    borderRadius: Border.br_xs,
    backgroundColor: Color.blanc,
    padding: Padding.p_11xl,
    flex: 1,
    flexDirection: 'column',
  },
  menuDropdown: {
    shadowColor: 'rgba(0, 0, 0, 0.2)',
    backgroundColor: Color.blanc,
    shadowOffset: {
      width: -10,
      height: 10,
    },
    shadowRadius: 40,
    elevation: 40,
    shadowOpacity: 1,
    flexDirection: 'row',
    borderRadius: Border.br_xs,
    justifyContent: 'flex-end',
    maxWidth: '100%',
    maxHeight: '100%',
    position: 'absolute',
    paddingVertical: Padding.p_lg,
  },
});
